import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';
import { useEntitiesStore } from './entities';
import type { Device } from '@/types';

export type ClimateSeasonKey = 'SUMMER' | 'WINTER' | 'TRANSITIONAL';
export type ClimateModeSource = 'none' | 'manual' | 'season';
export type ClimateSeasonSelectionMode = 'AUTO' | 'MANUAL';

export interface ClimateManagedDevice {
  id: string;
  deviceId: string;
  climateType: string;
  enabled: boolean;
  sortOrder: number;
  config?: Record<string, unknown> | null;
}

export interface ClimateModeDeviceConfig {
  targetTemperature?: number;
  /** HVAC mode — the KNX controller mode (heat / cool / auto / off …). */
  hvacMode?: string;
  /**
   * Operation mode — the KNX preset mode (auto / comfort / standby / economy /
   * building protection). A room thermostat carries only this one; an HVAC
   * unit may carry both.
   */
  operationMode?: string;
  fanMode?: string;
  humidityTarget?: number;
  /** Device participates in this mode (renamed from "Power On"). */
  enabled?: boolean;
  powerOn?: boolean;
  /**
   * @deprecated Covers moved to the Shades category — the sun controller is
   * configured per shade now. Field kept so stored mode configs stay valid.
   */
  sunController?: boolean;
}

export interface ClimateModePreset {
  id: string;
  name: string;
  icon: string;
  sortOrder: number;
  /** Per-season device presets: config[seasonKey][deviceId] = device config. */
  config: Record<string, Record<string, ClimateModeDeviceConfig>>;
}

/** Fixed id of the default (non-deletable) Protection mode. */
export const CLIMATE_PROTECTION_MODE_ID = 'protection';

/** Season preset for a mode (normalized to a flat device map). */
export function seasonConfigOf(
  mode: ClimateModePreset,
  seasonKey: ClimateSeasonKey,
): Record<string, ClimateModeDeviceConfig> {
  const cfg = mode?.config?.[seasonKey];
  return cfg && typeof cfg === 'object' ? cfg : {};
}

export interface ClimateSeasonPreset {
  id: string;
  seasonKey: ClimateSeasonKey;
  name: string;
  icon: string;
  sortOrder: number;
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
  defaultModeId: string | null;
}

export interface ClimateRuntimeState {
  effectiveModeId: string | null;
  activeSeasonId: string | null;
  activeSeasonKey: ClimateSeasonKey | null;
  seasonSelectionMode: ClimateSeasonSelectionMode;
  modeSource: ClimateModeSource;
}

export const CLIMATE_CONTROL_TYPES = [
  'climate',
  'fan',
  'humidifier',
  'sauna',
  'pool',
  'hot_tub',
];

/**
 * The retired `thermostat` class folded into Climate. Rows stored before the
 * merge still spell their climate type that way, so every read normalises it
 * rather than painting a second group for one class.
 */
export function normalizeClimateType(type: string | null | undefined): string {
  const value = String(type ?? '').toLowerCase();
  return value === 'thermostat' ? 'climate' : value;
}

export const CLIMATE_TYPE_ICONS: Record<string, string> = {
  climate: 'thermostat',
  thermostat: 'thermostat',
  fan: 'mode_fan',
  humidifier: 'water_drop',
  sauna: 'sauna',
  pool: 'pool',
  hot_tub: 'hot_tub',
};

/**
 * Each climate type's group heading in the app's own pair shape — one row per
 * enum value, its English and its Slovenian half — picked by
 * `getClimateTypeLabel()` below through `tt()`, so the heading follows a
 * language switch wherever the store is read (the climate route's group
 * headings, the settings page's lists and the mode editor's groups).
 * The keys are the API's enum values, compared in code and never painted.
 * Covers live in the Shades category and have no climate type here.
 */
export const CLIMATE_TYPE_LABELS: Record<string, [string, string]> = {
  climate: ['Climate', 'Klima'],
  fan: ['Fans', 'Ventilatorji'],
  humidifier: ['Humidifiers', 'Vlažilniki'],
  sauna: ['Saunas', 'Savne'],
  pool: ['Pools', 'Bazeni'],
  hot_tub: ['Hot Tubs', 'Masažne kadi'],
};

export function getClimateTypeIcon(type: string): string {
  return CLIMATE_TYPE_ICONS[type.toLowerCase()] ?? 'device_thermostat';
}

export function getClimateTypeLabel(type: string): string {
  const pair = CLIMATE_TYPE_LABELS[normalizeClimateType(type)];
  if (pair) return tt(pair[0], pair[1]);
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function detectClimateType(device: Device): string {
  const dc = (device.deviceControl ?? '').toLowerCase();
  if (CLIMATE_CONTROL_TYPES.includes(dc)) return dc;
  // A device still spellt `thermostat` is the same climate class.
  if (dc.includes('thermostat')) return 'climate';
  if (dc.includes('climate') || dc.includes('hvac')) return 'climate';
  if (dc.includes('fan')) return 'fan';
  if (dc.includes('sauna')) return 'sauna';
  if (dc.includes('hot_tub') || dc.includes('hottub') || dc.includes('jacuzzi')) return 'hot_tub';
  if (dc.includes('pool')) return 'pool';
  if (dc.includes('humid')) return 'humidifier';
  return 'climate';
}

export const useClimateStore = defineStore('climate', () => {
  const entitiesStore = useEntitiesStore();

  const hiddenDeviceIds = ref<string[]>([]);
  const managedDevices = ref<ClimateManagedDevice[]>([]);
  /** Backend scan list (GET /climate/scan) — climate/season values; covers belong to the Shades category. */
  const scanDevices = ref<Device[]>([]);
  const modes = ref<ClimateModePreset[]>([]);
  const seasons = ref<ClimateSeasonPreset[]>([]);
  const activeModeId = ref<string | null>(null);
  const effectiveModeId = ref<string | null>(null);
  const activeSeasonId = ref<string | null>(null);
  const activeSeasonKey = ref<ClimateSeasonKey | null>(null);
  const seasonSelectionMode = ref<ClimateSeasonSelectionMode>('AUTO');
  const manualSeasonId = ref<string | null>(null);
  const modeSource = ref<ClimateModeSource>('none');
  const settingsLoading = ref(false);
  const settingsSaving = ref(false);
  const error = ref<string | null>(null);

  const autoDetectedClimateDevices = computed<Device[]>(() =>
    entitiesStore.devices.filter((device) => {
      const dc = (device.deviceControl ?? '').toLowerCase();
      return (
        CLIMATE_CONTROL_TYPES.includes(dc) ||
        dc.includes('thermostat') ||
        dc.includes('climate') ||
        dc.includes('hvac') ||
        dc.includes('fan') ||
        dc.includes('sauna') ||
        dc.includes('pool') ||
        dc.includes('jacuzzi') ||
        dc.includes('humid')
      );
    }),
  );

  function buildSeedDevices(): ClimateManagedDevice[] {
    return [...autoDetectedClimateDevices.value]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((device, index) => ({
        id: device.id,
        deviceId: device.id,
        climateType: detectClimateType(device),
        enabled: !hiddenDeviceIds.value.includes(device.id),
        sortOrder: device.sortOrder ?? index,
        config: null,
      }));
  }

  const resolvedManagedDevices = computed<ClimateManagedDevice[]>(() => {
    if (managedDevices.value.length > 0) return managedDevices.value;
    return buildSeedDevices();
  });

  const compatibleDevices = computed<Device[]>(() => {
    const existingIds = new Set(resolvedManagedDevices.value.map((item) => item.deviceId));
    // Prefer the backend scan list (climate/season mode-category values;
    // covers are excluded by the hub). Falls back to the local heuristic until
    // the scan has been fetched.
    const source = scanDevices.value.length > 0 ? scanDevices.value : autoDetectedClimateDevices.value;
    return source.filter((device) => !existingIds.has(device.id));
  });

  const climateEntries = computed<Array<{ managed: ClimateManagedDevice; device: Device }>>(() =>
    resolvedManagedDevices.value
      // Legacy rows from before covers moved to the Shades category.
      .filter((managed) => (managed.climateType ?? '').toLowerCase() !== 'cover')
      .map((managed) => ({ managed, device: entitiesStore.getDevice(managed.deviceId) }))
      .filter((item): item is { managed: ClimateManagedDevice; device: Device } => Boolean(item.device))
      .sort((a, b) => (a.managed.sortOrder ?? 0) - (b.managed.sortOrder ?? 0)),
  );

  const climateDevices = computed<Device[]>(() =>
    climateEntries.value.filter((item) => item.managed.enabled).map((item) => item.device),
  );

  const visibleClimateDevices = computed<Device[]>(() => climateDevices.value);

  const allClimateDevices = computed<Device[]>(() => climateEntries.value.map((item) => item.device));

  const devicesByControlType = computed<Map<string, Device[]>>(() => {
    const map = new Map<string, Device[]>();
    for (const entry of climateEntries.value) {
      if (!entry.managed.enabled) continue;
      const type = normalizeClimateType(
        entry.managed.climateType || detectClimateType(entry.device),
      );
      if (!map.has(type)) map.set(type, []);
      map.get(type)!.push(entry.device);
    }
    for (const [, devices] of map) {
      devices.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    return new Map(
      [...map.entries()].sort(([a], [b]) => {
        const ai = CLIMATE_CONTROL_TYPES.indexOf(a);
        const bi = CLIMATE_CONTROL_TYPES.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      }),
    );
  });

  const allDevicesByControlType = computed<Map<string, Device[]>>(() => {
    const map = new Map<string, Device[]>();
    for (const entry of climateEntries.value) {
      const type = normalizeClimateType(
        entry.managed.climateType || detectClimateType(entry.device),
      );
      if (!map.has(type)) map.set(type, []);
      map.get(type)!.push(entry.device);
    }
    for (const [, devices] of map) {
      devices.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    return new Map(
      [...map.entries()].sort(([a], [b]) => {
        const ai = CLIMATE_CONTROL_TYPES.indexOf(a);
        const bi = CLIMATE_CONTROL_TYPES.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      }),
    );
  });

  const activeMode = computed<ClimateModePreset | null>(
    () => modes.value.find((mode) => mode.id === effectiveModeId.value) ?? null,
  );

  const activeSeason = computed<ClimateSeasonPreset | null>(
    () => seasons.value.find((season) => season.id === activeSeasonId.value) ?? null,
  );

  /** What `/climate/settings` answers (and the settings push carries). */
  interface HubClimateSettingsPayload {
    hiddenDeviceIds?: string[];
    devices?: ClimateManagedDevice[];
    modes?: ClimateModePreset[];
    seasons?: ClimateSeasonPreset[];
    activeModeId?: string | null;
    seasonSelectionMode?: ClimateSeasonSelectionMode;
    manualSeasonId?: string | null;
    modeSource?: ClimateModeSource;
    runtime?: {
      seasonSelectionMode?: ClimateSeasonSelectionMode;
      effectiveModeId?: string | null;
      activeSeasonId?: string | null;
      activeSeasonKey?: ClimateSeasonKey | null;
      modeSource?: ClimateModeSource;
    };
  }

  function applySettingsPayload(data: HubClimateSettingsPayload): void {
    hiddenDeviceIds.value = data.hiddenDeviceIds ?? [];
    // Rows written before the thermostat→climate merge carry the retired
    // type; the store reads them as climate so the app paints one class.
    managedDevices.value = (data.devices ?? []).map((device) => ({
      ...device,
      climateType: normalizeClimateType(device.climateType || 'climate') || 'climate',
    }));
    modes.value = data.modes ?? [];
    seasons.value = data.seasons ?? [];
    activeModeId.value = data.activeModeId ?? null;
    seasonSelectionMode.value = data.seasonSelectionMode ?? data.runtime?.seasonSelectionMode ?? 'AUTO';
    manualSeasonId.value = data.manualSeasonId ?? null;
    effectiveModeId.value = data.runtime?.effectiveModeId ?? data.activeModeId ?? null;
    activeSeasonId.value = data.runtime?.activeSeasonId ?? null;
    activeSeasonKey.value = data.runtime?.activeSeasonKey ?? null;
    modeSource.value = data.runtime?.modeSource ?? data.modeSource ?? 'none';
  }

  async function fetchSettings(): Promise<void> {
    settingsLoading.value = true;
    error.value = null;
    try {
      const data = await apiFetch('/climate/settings');
      applySettingsPayload(data);
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message ?? tt('Failed to load climate settings', 'Nastavitve klime ni bilo mogoče naložiti');
      console.error('[climateStore] fetchSettings error', err);
    } finally {
      settingsLoading.value = false;
    }
  }

  async function ensureManagedDevices(): Promise<void> {
    if (managedDevices.value.length > 0 || autoDetectedClimateDevices.value.length === 0) return;
    const seed = buildSeedDevices();
    settingsSaving.value = true;
    try {
      const data = await apiFetch('/climate/settings', {
        method: 'PATCH',
        body: JSON.stringify({ devices: seed }),
      });
      applySettingsPayload(data);
    } catch (err) {
      console.error('[climateStore] ensureManagedDevices error', err);
      managedDevices.value = seed;
    } finally {
      settingsSaving.value = false;
    }
  }

  async function fetchDevices(): Promise<void> {
    managedDevices.value = await apiFetch('/climate/devices');
  }

  async function addClimateDevice(data: {
    deviceId: string;
    climateType: string;
    enabled?: boolean;
    sortOrder?: number;
    config?: Record<string, unknown>;
  }): Promise<ClimateManagedDevice> {
    await ensureManagedDevices();
    settingsSaving.value = true;
    try {
      const device = await apiFetch('/climate/devices', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await fetchSettings();
      return device;
    } finally {
      settingsSaving.value = false;
    }
  }

  async function updateClimateDevice(
    deviceId: string,
    data: Partial<ClimateManagedDevice>,
  ): Promise<ClimateManagedDevice> {
    settingsSaving.value = true;
    try {
      const device = await apiFetch(`/climate/devices/${deviceId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      await fetchSettings();
      return device;
    } finally {
      settingsSaving.value = false;
    }
  }

  async function removeClimateDevice(deviceId: string): Promise<void> {
    settingsSaving.value = true;
    try {
      await apiFetch(`/climate/devices/${deviceId}`, { method: 'DELETE' });
      await fetchSettings();
    } finally {
      settingsSaving.value = false;
    }
  }

  async function reorderClimateDevices(deviceIds: string[]): Promise<void> {
    settingsSaving.value = true;
    try {
      managedDevices.value = await apiFetch('/climate/devices/reorder', {
        method: 'PATCH',
        body: JSON.stringify({ deviceIds }),
      });
    } finally {
      settingsSaving.value = false;
    }
  }

  async function fetchModes(): Promise<void> {
    modes.value = await apiFetch('/climate/modes');
  }

  async function createMode(data: {
    name: string;
    icon?: string;
    sortOrder?: number;
    config?: ClimateModePreset['config'];
  }): Promise<ClimateModePreset> {
    settingsSaving.value = true;
    try {
      const mode = await apiFetch('/climate/modes', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await fetchSettings();
      return mode;
    } finally {
      settingsSaving.value = false;
    }
  }

  async function updateMode(modeId: string, data: Partial<ClimateModePreset>): Promise<ClimateModePreset> {
    settingsSaving.value = true;
    try {
      const mode = await apiFetch(`/climate/modes/${modeId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      await fetchSettings();
      return mode;
    } finally {
      settingsSaving.value = false;
    }
  }

  async function reorderClimateModes(modeIds: string[]): Promise<void> {
    try {
      const updated = await apiFetch('/climate/modes/reorder', {
        method: 'PATCH',
        body: JSON.stringify({ modeIds }),
      });
      if (Array.isArray(updated)) modes.value = updated;
    } catch (err) {
      console.error('[ClimateStore] reorderClimateModes error', err);
      throw err;
    }
  }

  /**
   * Load the built-in default arm modes (Presence / Sleep / Absence / Away).
   * Idempotent — modes that already exist (by fixed id) are left untouched.
   */
  async function loadDefaultModes(): Promise<{ created: string[] }> {
    settingsSaving.value = true;
    try {
      const result = await apiFetch('/climate/modes/defaults', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (Array.isArray(result?.modes)) {
        modes.value = result.modes;
      } else {
        await fetchSettings();
      }
      return { created: Array.isArray(result?.created) ? result.created : [] };
    } finally {
      settingsSaving.value = false;
    }
  }

  /** Fetch the backend scan list (climate/season values; covers only with
   *  sun controller). */
  async function fetchClimateScan(): Promise<void> {
    try {
      const devices = await apiFetch('/climate/scan');
      if (Array.isArray(devices)) scanDevices.value = devices;
    } catch (err) {
      console.error('[ClimateStore] fetchClimateScan error', err);
    }
  }

  /** A device the sync dropped, as the hub reports it. */
  interface RemovedDeviceRef {
    deviceId?: string;
    deviceName?: string;
    [key: string]: unknown;
  }

  /** Reconcile managed devices with the backend scan list. */
  async function syncClimateDevices(): Promise<{
    added: ClimateManagedDevice[];
    removed: RemovedDeviceRef[];
    devices: ClimateManagedDevice[];
  }> {
    const result = await apiFetch('/climate/devices/sync', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    if (Array.isArray(result.devices)) managedDevices.value = result.devices;
    void fetchClimateScan();
    return result;
  }

  async function deleteMode(modeId: string): Promise<void> {
    settingsSaving.value = true;
    try {
      await apiFetch(`/climate/modes/${modeId}`, { method: 'DELETE' });
      await fetchSettings();
    } finally {
      settingsSaving.value = false;
    }
  }

  async function activateMode(modeId: string): Promise<void> {
    settingsSaving.value = true;
    try {
      await apiFetch(`/climate/modes/${modeId}/activate`, { method: 'POST' });
      await fetchSettings();
    } finally {
      settingsSaving.value = false;
    }
  }

  async function clearManualMode(): Promise<void> {
    settingsSaving.value = true;
    try {
      await apiFetch('/climate/manual', { method: 'POST' });
      await fetchSettings();
    } finally {
      settingsSaving.value = false;
    }
  }

  async function fetchSeasons(): Promise<void> {
    const data = await apiFetch('/climate/seasons');
    seasons.value = data.seasons ?? [];
    seasonSelectionMode.value = data.selectionMode ?? 'AUTO';
    manualSeasonId.value = data.manualSeasonId ?? null;
    activeSeasonId.value = data.runtime?.activeSeasonId ?? null;
    activeSeasonKey.value = data.runtime?.activeSeasonKey ?? null;
    effectiveModeId.value = data.runtime?.effectiveModeId ?? effectiveModeId.value;
    modeSource.value = data.runtime?.modeSource ?? modeSource.value;
  }

  async function updateSeason(seasonId: string, data: Partial<ClimateSeasonPreset>): Promise<ClimateSeasonPreset> {
    settingsSaving.value = true;
    try {
      const season = await apiFetch(`/climate/seasons/${seasonId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      await fetchSettings();
      return season;
    } finally {
      settingsSaving.value = false;
    }
  }

  async function setSeasonManual(seasonId: string): Promise<void> {
    settingsSaving.value = true;
    try {
      await apiFetch('/climate/seasons/manual', {
        method: 'POST',
        body: JSON.stringify({ seasonId }),
      });
      await fetchSettings();
    } finally {
      settingsSaving.value = false;
    }
  }

  async function setSeasonAuto(): Promise<void> {
    settingsSaving.value = true;
    try {
      await apiFetch('/climate/seasons/auto', { method: 'POST' });
      await fetchSettings();
    } finally {
      settingsSaving.value = false;
    }
  }

  async function resolveActiveSeason(): Promise<ClimateRuntimeState> {
    const runtime = await apiFetch('/climate/seasons/resolve', { method: 'POST' });
    activeSeasonId.value = runtime.activeSeasonId ?? null;
    activeSeasonKey.value = runtime.activeSeasonKey ?? null;
    effectiveModeId.value = runtime.effectiveModeId ?? null;
    modeSource.value = runtime.modeSource ?? 'none';
    seasonSelectionMode.value = runtime.seasonSelectionMode ?? seasonSelectionMode.value;
    return runtime;
  }

  async function saveSettings(): Promise<void> {
    settingsSaving.value = true;
    try {
      const data = await apiFetch('/climate/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          hiddenDeviceIds: hiddenDeviceIds.value,
          devices: managedDevices.value,
          modes: modes.value,
          activeModeId: activeModeId.value,
          modeSource: modeSource.value,
          seasons: seasons.value,
          seasonSelectionMode: seasonSelectionMode.value,
          manualSeasonId: manualSeasonId.value,
        }),
      });
      applySettingsPayload(data);
    } finally {
      settingsSaving.value = false;
    }
  }

  async function toggleHidden(deviceId: string): Promise<void> {
    await ensureManagedDevices();
    const managed = managedDevices.value.find((item) => item.deviceId === deviceId || item.id === deviceId);
    if (managed) {
      await updateClimateDevice(managed.id, { enabled: !managed.enabled });
      return;
    }

    const idx = hiddenDeviceIds.value.indexOf(deviceId);
    if (idx === -1) hiddenDeviceIds.value.push(deviceId);
    else hiddenDeviceIds.value.splice(idx, 1);
    await saveSettings();
  }

  function isHidden(deviceId: string): boolean {
    const managed = resolvedManagedDevices.value.find((item) => item.deviceId === deviceId || item.id === deviceId);
    if (managed) return !managed.enabled;
    return hiddenDeviceIds.value.includes(deviceId);
  }

  async function loadAll(): Promise<void> {
    await fetchSettings();
  }

  return {
    hiddenDeviceIds,
    managedDevices,
    modes,
    seasons,
    activeModeId,
    effectiveModeId,
    activeSeasonId,
    activeSeasonKey,
    seasonSelectionMode,
    manualSeasonId,
    modeSource,
    settingsLoading,
    settingsSaving,
    error,
    resolvedManagedDevices,
    climateEntries,
    compatibleDevices,
    climateDevices,
    visibleClimateDevices,
    allClimateDevices,
    devicesByControlType,
    allDevicesByControlType,
    activeMode,
    activeSeason,
    fetchSettings,
    fetchDevices,
    addClimateDevice,
    updateClimateDevice,
    removeClimateDevice,
    reorderClimateDevices,
    fetchModes,
    createMode,
    updateMode,
    reorderClimateModes,
    loadDefaultModes,
    deleteMode,
    activateMode,
    clearManualMode,
    fetchClimateScan,
    syncClimateDevices,
    fetchSeasons,
    updateSeason,
    setSeasonManual,
    setSeasonAuto,
    resolveActiveSeason,
    saveSettings,
    toggleHidden,
    isHidden,
    ensureManagedDevices,
    loadAll,
  };
});
